import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { ArrowLeft, Loader2, Play, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import UploadDropzone from '@/components/upload/UploadDropzone';
import SelectedMediaCard from '@/components/upload/SelectedMediaCard';
import AnalysisOptionsPanel from '@/components/upload/AnalysisOptionsPanel';
import { selectedFileAtom, analysisModeAtom, autoDeleteAtom, previewUrlAtom, previewMediaTypeAtom } from '@/hooks/atoms';
import { useCreateJob, useStartAnalysis } from '@/hooks/useJobs';

export default function UploadPage() {
  const navigate = useNavigate();
  const [file, setFile] = useAtom(selectedFileAtom);
  const mode = useAtomValue(analysisModeAtom);
  const autoDelete = useAtomValue(autoDeleteAtom);
  const setPreviewUrl = useSetAtom(previewUrlAtom);
  const setPreviewMediaType = useSetAtom(previewMediaTypeAtom);
  const { createJob } = useCreateJob();
  const { startAnalysis } = useStartAnalysis();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleRunAnalysis = async () => {
    if (!file) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      // Create object URL before clearing file so the viewer can display it
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      setPreviewMediaType(file.type.startsWith('video/') ? 'video' : 'image');

      const { jobId } = await createJob(file, mode, autoDelete);
      await startAnalysis(jobId);
      setFile(null);
      navigate(`/analyze/${jobId}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Failed to start analysis');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewDemo = () => {
    navigate('/analyze/job_demo_001');
  };

  return (
    <div className="max-w-2xl mx-auto py-12 px-4">
      <Link
        to="/"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Dashboard
      </Link>

      <div className="text-center mb-8">
        <Shield className="h-12 w-12 text-primary mx-auto mb-4" />
        <h1 className="text-3xl font-bold text-foreground">Media Forensics Analysis</h1>
        <p className="text-muted-foreground mt-2">
          Upload an image or video to detect manipulation, AI-generated content, and suspicious artifacts.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Upload Media</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {!file ? (
            <UploadDropzone />
          ) : (
            <>
              <SelectedMediaCard file={file} />
              <Separator />
              <AnalysisOptionsPanel />
              <Separator />

              {submitError && (
                <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <p className="text-sm text-destructive">{submitError}</p>
                </div>
              )}

              <Button onClick={handleRunAnalysis} disabled={isSubmitting} className="w-full" size="lg">
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Starting Analysis...
                  </>
                ) : (
                  <>
                    <Play className="h-4 w-4 mr-2" />
                    Run Analysis
                  </>
                )}
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      <div className="mt-6 text-center">
        <button onClick={handleViewDemo} className="text-sm text-muted-foreground hover:text-primary transition-colors">
          View demo analysis result
        </button>
      </div>
    </div>
  );
}
